<script setup lang="ts">
  import { computed, useTemplateRef } from 'vue'
  import { useFormatting } from '@gat-ship/utils'
  import { testIds } from '@gat-ship/types'
  import {
    Button,
    ModalSection,
    Tooltip,
    TooltipTrigger,
    TooltipContent,
    useAppLoading
  } from '@gat-ship/vue-components'
  import {
    tenantUserLoadingIds,
    useTenantUserDetailContext
  } from '../../composables/useTenantUserDetail'
  import { useTenantUserModals } from '../providers'
  import TenantUserAvatar from '../avatar/TenantUserAvatar.vue'
  import ProfileImageFormChange from '../forms/ProfileImageFormChange.vue'

  const { formatPhoneNumber } = useFormatting()

  const tenantUserProvider = useTenantUserDetailContext()!
  const modals = useTenantUserModals()!
  const { isLoading: appIsLoading } = useAppLoading()

  const tenantUser = computed(() => tenantUserProvider.tenantUser.value)

  const formChangeProfileImage = useTemplateRef('formChangeProfileImage')

  const isChangingName = computed(() =>
    appIsLoading([tenantUserLoadingIds.changeName])
  )
  const isChangingJobTitle = computed(() =>
    appIsLoading([tenantUserLoadingIds.changeJobTitle])
  )
  const isChangingEmail = computed(() =>
    appIsLoading([tenantUserLoadingIds.changeEmail])
  )
  const isChangingPhone = computed(() =>
    appIsLoading([tenantUserLoadingIds.changePhoneNumber])
  )
  const isChangingProfileImage = computed(() =>
    appIsLoading([tenantUserLoadingIds.changeProfileImage])
  )

  const phoneButtonLabel = computed(() =>
    tenantUser.value?.mobilePhone ? 'Update Phone Number' : 'Add Phone Number'
  )
</script>

<template>
  <ModalSection v-if="tenantUser">
    <div class="divide-y">
      <div class="flex items-start justify-between gap-4 py-3">
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium">Profile Image</span>
        </div>
        <div class="flex shrink-0 flex-col items-end gap-2">
          <TenantUserAvatar
            class="size-12"
            :tenant-user="tenantUser"
            loading-id="add-profile-image"
          />
          <Tooltip>
            <TooltipTrigger>
              <Button
                v-if="tenantUser.canChangeProfileImage"
                variant="outline"
                size="sm"
                class="w-fit"
                :disabled="isChangingProfileImage"
                :data-testid="testIds.tenantUser.actions.changeProfileImage"
                @click="formChangeProfileImage?.onUploadPhotoClicked()"
              >
                Update Image
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Upload or update your profile picture (square images work best).
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      <div class="flex items-start justify-between gap-4 py-3">
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium">Full Name</span>
        </div>
        <div class="flex shrink-0 flex-col items-end gap-2">
          <span
            class="text-muted-foreground text-sm"
            :data-testid="testIds.tenantUser.details.userName"
          >
            {{ tenantUser.fullName }} ({{ tenantUser.initials }})
          </span>
          <Tooltip>
            <TooltipTrigger>
              <Button
                v-if="tenantUser.canChangeName"
                variant="outline"
                size="sm"
                class="w-fit"
                :disabled="isChangingName"
                :data-testid="testIds.tenantUser.actions.changeName"
                @click="modals.showChangeName({ tenantUser })"
              >
                Update Name
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Update the user's name as it appears in the system.
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      <div class="flex items-start justify-between gap-4 py-3">
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium">Job Title</span>
        </div>
        <div class="flex shrink-0 flex-col items-end gap-2">
          <span
            class="text-muted-foreground text-sm"
            :data-testid="testIds.tenantUser.details.userTitle"
          >
            {{ tenantUser.jobTitle ?? 'No job title' }}
          </span>
          <Tooltip>
            <TooltipTrigger>
              <Button
                v-if="tenantUser.isActive"
                variant="outline"
                size="sm"
                class="w-fit"
                :disabled="isChangingJobTitle"
                :data-testid="testIds.tenantUser.actions.changeTitle"
                @click="modals.showChangeJobTitle({ tenantUser })"
              >
                Update Job Title
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Update the user's role or position as displayed in the system.
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      <div class="flex items-start justify-between gap-4 py-3">
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium">Username (Email Address)</span>
        </div>
        <div class="flex shrink-0 flex-col items-end gap-2">
          <span
            class="text-muted-foreground text-sm"
            :data-testid="testIds.tenantUser.details.userEmail"
          >
            {{ tenantUser.email }}
          </span>
          <Tooltip>
            <TooltipTrigger>
              <Button
                v-if="tenantUser.canChangeEmail"
                variant="outline"
                size="sm"
                class="w-fit"
                :disabled="isChangingEmail"
                :data-testid="testIds.tenantUser.actions.changeEmail"
                @click="modals.showChangeEmail({ tenantUser })"
              >
                Change Username
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Update the email address the user uses to sign in.
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      <div class="flex items-start justify-between gap-4 py-3">
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium">Phone Number</span>
        </div>
        <div class="flex shrink-0 flex-col items-end gap-2">
          <span
            class="text-muted-foreground text-sm"
            :data-testid="testIds.tenantUser.details.userPhone"
          >
            {{ formatPhoneNumber(tenantUser.mobilePhone) ?? 'No phone number' }}
          </span>
          <Tooltip>
            <TooltipTrigger>
              <Button
                v-if="tenantUser.canChangeMobilePhoneNumber"
                variant="outline"
                size="sm"
                class="w-fit"
                :disabled="isChangingPhone"
                :data-testid="testIds.tenantUser.actions.changePhone"
                @click="modals.showChangePhoneNumber({ tenantUser })"
              >
                {{ phoneButtonLabel }}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Update the user's phone number, preferably their business number.
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
    <ProfileImageFormChange
      ref="formChangeProfileImage"
      class="hidden"
      :tenant-user="tenantUser"
      :loading-ids="['add-profile-image']"
    />
  </ModalSection>
</template>
